import { GridItem, Text, VStack } from "@chakra-ui/react";
import { formatDate } from "@syla/shared/helpers/formatting";
import {
  PlanStatus,
  getSubscriptionState,
} from "@syla/shared/types/helpers/subscriptions";
import { PlanBase } from "@syla/shared/types/models/PlanBase";
import { SubscriptionWithPlansAndAccess } from "@syla/shared/types/responses/GetSubscriptionsResponse";
import React from "react";
import { PlanAlert } from "./PlanAlert";

interface SubscriptionAlertProps {
  canManageSubscription: boolean;
  subscription?: SubscriptionWithPlansAndAccess<string>;
  downgradePlan?: PlanBase<string>;
  isRedirecting: boolean;
  onManageBillingAction: () => Promise<void>;
}

export const SubscriptionAlert = ({
  canManageSubscription,
  subscription,
  downgradePlan,
  isRedirecting,
  onManageBillingAction,
}: SubscriptionAlertProps) => {
  const subscriptionStatus = subscription && getSubscriptionState(subscription);

  const planChangeDate =
    subscriptionStatus ?
      formatDate(
        (
          [
            PlanStatus.Active,
            PlanStatus.DowngradeScheduled,
            PlanStatus.RequiresAction,
          ].includes(subscriptionStatus.planStatus)
        ) ?
          subscriptionStatus.renewsOn
        : subscriptionStatus.lapsesOn,
      )
    : undefined;

  if (!subscriptionStatus) {
    return null;
  }

  if (subscriptionStatus.planStatus === PlanStatus.Expired) {
    return (
      <GridItem colSpan={2}>
        <PlanAlert
          title="Your plan has expired"
          description={
            <>
              Your previous plan is no longer available for renewal.
              <br />
              Please purchase a new plan to continue using Syla.
            </>
          }
        />
      </GridItem>
    );
  }

  if (
    canManageSubscription &&
    subscriptionStatus.planStatus === PlanStatus.RequiresAction
  ) {
    return (
      <GridItem colSpan={2}>
        <PlanAlert
          title="There's an issue with the billing on your account"
          description={
            <>
              Please update your billing details to retain access to your
              account
            </>
          }
          action={{
            label: "Update billing details",
            onClickAction: onManageBillingAction,
            isDisabled: isRedirecting,
          }}
        />
      </GridItem>
    );
  }

  if (
    canManageSubscription &&
    subscriptionStatus.planStatus === PlanStatus.Cancelled &&
    planChangeDate
  ) {
    const resumeDescription = (
      <VStack align="start" spacing={2}>
        <Text>
          Your subscription will expire on <b>{planChangeDate}</b>.
          <br />
          Resume your plan to retain access to your account and avoid losing
          your current pricing and features.
        </Text>

        {!subscription?.plan.active && (
          <Text>
            <Text
              color="error.500"
              fontWeight="bold"
              as="span"
              display="inline"
            >
              Important:
            </Text>{" "}
            Your legacy plan cannot be resumed once it expires. <br /> If your
            plan lapses, you’ll need to rejoin on a new plan, at the current
            pricing.
          </Text>
        )}
      </VStack>
    );

    return (
      <GridItem colSpan={2}>
        <PlanAlert
          title="Your plan has been cancelled"
          description={resumeDescription}
        />
      </GridItem>
    );
  }

  if (downgradePlan && planChangeDate) {
    return (
      <GridItem colSpan={2}>
        <PlanAlert
          title="Your plan will downgrade"
          description={
            <>
              Your subscription will downgrade to the{" "}
              <b>{downgradePlan.name}</b> plan on <b>{planChangeDate}</b>.
              <br />
              Contact support if you would like the downgrade to take effect
              immediately.
            </>
          }
        />
      </GridItem>
    );
  }

  return null;
};
